import { useEffect, useRef } from 'react';

// ANX-044 — глобальный keydown-listener без зависимостей.
//
// Соглашения по ключам:
//  - "mod" — cmd на macOS, ctrl на Win/Linux. Принимаем оба для кросс-платформенности.
//  - Обычные символы → `e.key.toLowerCase()`; именованные (Enter/Escape/…) — тоже lowercase.
//  - Спец-символы `?`/`/` приходят printable, отдельный `shift+` не нужен.
//  - По умолчанию хоткей пропускается, если фокус на input/textarea/select/contenteditable.
//    Через `allowInEditable: [...]` можно разрешить конкретные комбинации.

export type Hotkey = string;
export type HotkeyHandler = (e: KeyboardEvent) => void;
export type HotkeyMap = Record<Hotkey, HotkeyHandler>;

export interface UseHotkeysOptions {
  enabled?: boolean;
  allowInEditable?: Hotkey[];
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // isContentEditable считаем через свойство в реальных браузерах и через
  // contentEditable IDL string (fallback для happy-dom, где
  // isContentEditable=false без layout-pass).
  if (target.isContentEditable) return true;
  if (target.contentEditable === 'true' || target.contentEditable === 'plaintext-only') return true;
  return false;
}

function normalize(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  parts.push(e.key.toLowerCase());
  return parts.join('+');
}

export function useHotkeys(map: HotkeyMap, opts: UseHotkeysOptions = {}): void {
  // Держим map в ref, чтобы обновления handler'ов не пересоздавали listener.
  const mapRef = useRef(map);
  mapRef.current = map;

  const enabled = opts.enabled !== false;
  const allowInEditable = opts.allowInEditable;

  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const key = normalize(e);
      const handler = mapRef.current[key];
      if (!handler) return;
      if (isEditable(e.target) && !(allowInEditable && allowInEditable.includes(key))) return;
      e.preventDefault();
      handler(e);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // allowInEditable — стабильный список из props; пересоздаём listener только при изменении.
  }, [enabled, allowInEditable]);
}
